export const ROLES = ['ADMIN', 'ATTENDANT', 'TECHNICIAN'] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  ADMIN: 'Administrador',
  ATTENDANT: 'Atendente',
  TECHNICIAN: 'Técnico',
};

/**
 * Permissões por perfil. Os guards do backend e o menu do frontend usam a mesma tabela.
 * - ADMIN: tudo, inclusive usuários, configurações, exclusões e importação.
 * - ATTENDANT: balcão — clientes, equipamentos, OS, pagamentos e catálogo.
 * - TECHNICIAN: bancada — consulta clientes, trabalha nas OS (diagnóstico, itens, fotos, histórico, status)
 *   e cadastra/consulta componentes eletrônicos.
 * - Estoque (quantidades) e financeiro: somente ADMIN.
 */
export const PERMISSIONS = {
  'clients.read': ['ADMIN', 'ATTENDANT', 'TECHNICIAN'],
  'clients.write': ['ADMIN', 'ATTENDANT'],
  'clients.delete': ['ADMIN'],
  'equipment.read': ['ADMIN', 'ATTENDANT', 'TECHNICIAN'],
  'equipment.write': ['ADMIN', 'ATTENDANT', 'TECHNICIAN'],
  'equipment.delete': ['ADMIN'],
  'orders.read': ['ADMIN', 'ATTENDANT', 'TECHNICIAN'],
  'orders.write': ['ADMIN', 'ATTENDANT', 'TECHNICIAN'],
  'orders.delete': ['ADMIN'],
  'orders.secrets': ['ADMIN', 'ATTENDANT', 'TECHNICIAN'],
  'payments.write': ['ADMIN', 'ATTENDANT'],
  'payments.refund': ['ADMIN'],
  'catalog.read': ['ADMIN', 'ATTENDANT', 'TECHNICIAN'],
  'catalog.write': ['ADMIN', 'ATTENDANT', 'TECHNICIAN'],
  'catalog.delete': ['ADMIN'],
  'categories.manage': ['ADMIN'],
  'components.read': ['ADMIN', 'ATTENDANT', 'TECHNICIAN'],
  'components.write': ['ADMIN', 'TECHNICIAN'],
  'components.delete': ['ADMIN'],
  /** Alterar quantidade em estoque manualmente (compra/entrada e contagem/ajuste). */
  'stock.adjust': ['ADMIN'],
  /** Área financeira (faturamento, custo, lucro). */
  'finance.view': ['ADMIN'],
  'dashboard.read': ['ADMIN', 'ATTENDANT', 'TECHNICIAN'],
  'users.manage': ['ADMIN'],
  'settings.manage': ['ADMIN'],
  'import.run': ['ADMIN'],
} as const satisfies Record<string, readonly Role[]>;

export type Permission = keyof typeof PERMISSIONS;

export function can(role: Role | undefined | null, permission: Permission): boolean {
  return !!role && (PERMISSIONS[permission] as readonly Role[]).includes(role);
}
